import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import ReportAction from '@nile/reports/src/modules/reports/components/subpages/ReportAction';
import ReportInfoToolbar from '@nile/reports/src/modules/reports/components/subpages/ReportToolbar/ReportInfoToolbar';
import ReportHeader from '@nile/reports/src/modules/reports/components/subpages/ReportHeader';
import ReportFilter from '@nile/reports/src/modules/reports/components/subpages/ReportFilter';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { isReportSaved } from '@nile/reports/src/modules/reports/helper';
import {
  EDIT_MODE,
  VIEW_MODE,
  CREATE_MODE,
  REPORTS,
  EFFICIENCY_REPORT,
  PDF_REPORT,
  PROFITABLITY_REPORT,
  RETAINER
} from '@nile/reports/src/modules/home/constants';
import {
  Button,
  DisplayText,
  Modal,
  ToggleButton
} from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { UPDATE_REPORT } from '@nile/reports/src/modules/reports/graphql';
import { useMutation } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { useActivityTracker } from '@nile/components';

const ReportToolbar = (props) => {
  const {
    handleSave,
    errors,
    submitActionLoading,
    history,
    reportType,
    activeViewName,
    reportInternalType
  } = props;
  const reportsContext = useContext(ReportsContext);
  const tabContext = useContext(TabContext);

  const [updateReport] = useMutation(UPDATE_REPORT, {
    client: AccountClient,
    onCompleted({ updateReport }) {
      reportsContext?.updateReportDataContext(updateReport);
    }
  });

  const [closeConfirmation, setCloseConfirmation] = useState(false);
  const [isTvDashboardEnabled, setIsTvDashboardEnabled] = useState(
    reportsContext.isTvDashboardEnabled
  );

  useEffect(() => {
    setIsTvDashboardEnabled(reportsContext?.isTvDashboardEnabled);
  }, [reportsContext?.isTvDashboardEnabled]);

  const onCancel = () => {
    if (reportsContext.activeReport.reportId) {
      history.push(`/report/${reportsContext.activeReport.reportId || 1}/view`);
    } else {
      if (tabContext?.activeTab?.state?.source || history?.length <= 2) {
        history.push(`/report/1/view`);
        tabContext.updateTab({
          module: 'REPORTS_CHARTS_HOME',
          state: {
            source: null
          }
        });
        return;
      }
      history.goBack();
    }
  };

  const cancelReport = () => {
    setCloseConfirmation(false);
    if (reportsContext.cloneId) {
      reportsContext.setCloneId(null);
      reportsContext.dispatch({
        type: 'updateLayout',
        layout: []
      });
    }
    onCancel();
  };

  const closeConfirmationModal = () => {
    const newWithLayout = {
      ...reportsContext.activeReport,
      layout: reportsContext.layout
    };
    const isSaved = isReportSaved(
      reportsContext.activeSection.data,
      newWithLayout
    );
    if (!isSaved) {
      setCloseConfirmation(true);
      return;
    }
    if (reportsContext.cloneId) reportsContext.setCloneId(null);
    onCancel();
  };

  const checkDateFilter = () => {
    return reportType !== PDF_REPORT && reportInternalType !== RETAINER;
  };

  const notViewMode =
    reportsContext.reportViewMode === EDIT_MODE ||
    reportsContext.reportViewMode === CREATE_MODE;

  const showClientFilter =
    reportType !== EFFICIENCY_REPORT && reportType !== PROFITABLITY_REPORT;

  const getReportFilterVisiblity = () => {
    return !notViewMode && reportType !== PDF_REPORT;
  };

  const showReportFilter = getReportFilterVisiblity();
  const showActions =
    reportsContext.reportViewMode !== CREATE_MODE && showClientFilter;

  return (
    <div className="fx actr h100 bg-white p20 divider">
      {notViewMode ? (
        <ReportHeader
          activeView={reportsContext.activeSection.data}
          errors={errors}
          isViewOnly={reportsContext.reportViewMode === VIEW_MODE}
        />
      ) : (
        <ReportInfoToolbar
          toggleNavMenu={() =>
            reportsContext.setNavOpen(!reportsContext.navOpen)
          }
          context={reportsContext}
          activeView={reportsContext.activeSection.data}
          reportType={reportType}
          activeViewName={activeViewName}
        />
      )}
      <div className="flex f11 jend actr g10">
        {reportType === REPORTS &&
          reportInternalType !== RETAINER &&
          reportsContext.reportViewMode !== CREATE_MODE &&
          reportsContext?.isReportEditAuthorised && (
            <>
              <DisplayText size="large">
                <FormattedMessage id="reports.tvMode.toggle.label" />
              </DisplayText>
              <ToggleButton
                value={isTvDashboardEnabled}
                onChange={() => {
                  setIsTvDashboardEnabled(!isTvDashboardEnabled);
                  reportsContext.setNavOpen(
                    isTvDashboardEnabled ? true : false
                  );
                  useActivityTracker({
                    event: 'Tv Dashboard Toggle Click'
                  });
                  updateReport({
                    variables: {
                      reportId: reportsContext.activeReport.reportId,
                      input: {
                        fullScreenModeEnabled: !isTvDashboardEnabled,
                        fullScreenModeRefreshInterval: !reportsContext?.tvRefreshRate
                          ? 10
                          : reportsContext?.tvRefreshRate
                      }
                    }
                  });
                }}
                locked={notViewMode}
                size="small"
              />
            </>
          )}
        {showReportFilter && (
          <ReportFilter
            reportType={reportType}
            showSite={false}
            showDateFilter={checkDateFilter()}
            showClient={showClientFilter}
          />
        )}
        {showActions && (
          <ReportAction
            isEditMode={reportsContext.reportViewMode === EDIT_MODE}
            reportType={reportType}
            reportInternalType={reportInternalType}
          />
        )}
        {notViewMode && (
          <div className="fx actr">
            <Modal
              activator={
                <Button
                  plain
                  onClick={() => closeConfirmationModal()}
                  monochrome
                >
                  <FormattedMessage id="common.cancel" />
                </Button>
              }
              open={closeConfirmation}
              onClose={() => setCloseConfirmation(false)}
              variant="confirmation"
              title={
                <FormattedMessage id="reports.chart.create.modal.discardChanges" />
              }
              primaryAction={{
                content: <FormattedMessage id="common.button.discardChanges" />,
                onAction: () => cancelReport()
              }}
              secondaryActions={[
                {
                  content: <FormattedMessage id="common.cancel" />,
                  onAction: () => setCloseConfirmation(false)
                }
              ]}
            />
          </div>
        )}
        {notViewMode && (
          <Button
            loading={submitActionLoading}
            onClick={() => {
              handleSave(false);
            }}
            icon={<SVGIcon name="Save" />}
            primary
          >
            <FormattedMessage id="common.save" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default withRouter(ReportToolbar);
